// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Edit, Trash2 } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Skeleton, Modal } from '@/components/ui'
import { useAnnouncementDetail, useDeleteAnnouncement } from '@/hooks/useAnnouncements'
import { useDateTimeFormatter } from '@/hooks/useDateFormatter'
import type { Announcement, AnnouncementStatus } from '@/types'

export function AnnouncementDetailPage() {
  const { t } = useTranslation('announcements')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const announcementId = Number(id)
  const navigate = useNavigate()
  const formatDateTime = useDateTimeFormatter()

  const { data: announcement, isLoading, isFetching } = useAnnouncementDetail(announcementId)
  const deleteAnnouncement = useDeleteAnnouncement()

  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const handleDelete = async () => {
    await deleteAnnouncement.mutateAsync(announcementId)
    navigate('/announcements')
  }

  const getStatusBadge = (a: Announcement) => {
    const variants: Record<AnnouncementStatus, 'success' | 'warning' | 'ghost'> = {
      active: 'success',
      scheduled: 'warning',
      expired: 'ghost',
    }
    return <Badge variant={variants[a.status]}>{t(`status.${a.status}`)}</Badge>
  }

  const getTargetLabel = (a: Announcement) => {
    if (a.target_type === 'all') return t('target.all')
    return a.target_label || '-'
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Card>
          <Skeleton className="h-48" />
        </Card>
      </div>
    )
  }

  if (!announcement) {
    return (
      <Card>
        <EmptyState
          title={tc('errors.something_went_wrong')}
          action={
            <Button onClick={() => navigate('/announcements')}>{t('title')}</Button>
          }
        />
      </Card>
    )
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: t('title'), href: '/announcements' },
            { label: announcement.title },
          ]}
        />

        <PageHeader
          title={announcement.title}
          isRefreshing={isFetching && !isLoading}
          primaryAction={{
            key: 'edit',
            label: tc('actions.edit'),
            icon: Edit,
            to: `/announcements/${id}/edit`,
          }}
          overflowActions={[
            {
              key: 'delete',
              label: tc('actions.delete'),
              icon: Trash2,
              destructive: true,
              onClick: () => setShowDeleteModal(true),
            },
          ]}
        />

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Body card */}
          <Card className="lg:col-span-2">
            <p className="whitespace-pre-wrap text-base-content">{announcement.body}</p>
          </Card>

          {/* Details sidebar */}
          <Card>
            <dl className="space-y-4">
              <div>
                <dt className="text-sm text-base-content/60">{tc('labels.status')}</dt>
                <dd className="mt-1">{getStatusBadge(announcement)}</dd>
              </div>
              <div>
                <dt className="text-sm text-base-content/60">{t('form.target_label')}</dt>
                <dd className="font-medium mt-1">{getTargetLabel(announcement)}</dd>
              </div>
              <div>
                <dt className="text-sm text-base-content/60">{t('detail.published')}</dt>
                <dd className="font-medium mt-1">
                  {formatDateTime(announcement.published_at)}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-base-content/60">{t('detail.expires')}</dt>
                <dd className="font-medium mt-1">
                  {announcement.expires_at
                    ? formatDateTime(announcement.expires_at)
                    : t('detail.no_expiry')}
                </dd>
              </div>
              {announcement.created_by && (
                <div>
                  <dt className="text-sm text-base-content/60">{t('detail.created_by')}</dt>
                  <dd className="font-medium mt-1">{announcement.created_by.name}</dd>
                </div>
              )}
            </dl>
          </Card>
        </div>
      </div>

      {/* Delete confirmation modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={tc('actions.delete')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              variant="error"
              onClick={handleDelete}
              loading={deleteAnnouncement.isPending}
            >
              {tc('actions.delete')}
            </Button>
          </>
        }
      >
        <p>{t('form.delete_confirm')}</p>
      </Modal>
    </>
  )
}
